// src/oura/board/requests.ts
//
// Each client's change requests, for the board's "New request" note. One read
// per client, GET /change-requests?businessId= (the newest open request, and the
// counts for that client). A client whose read fails says so; it is never shown
// as zero. Production shows none while REQUESTS_ON is off.

import { useCallback, useEffect, useRef, useState } from "react";
import { listChangeRequests, type ChangeRequestRow } from "@service/aeo/changeRequests";
import { REQUESTS_ON } from "../requests/flag";

export interface ClientRequests {
    /** Not picked up yet (Needs us). */
    freshCount: number;
    /** The newest of those, for the note's example. */
    latest: ChangeRequestRow | null;
    /** Every request not done. */
    open: number;
}

export type BoardRequests =
    | { state: "loading" }
    | { state: "ready"; data: ClientRequests }
    | { state: "failed"; message: string };

/** Four clients' reads at a time. */
const PARALLEL = 4;

export async function requestsOf(businessId: string): Promise<BoardRequests> {
    const res = await listChangeRequests({ businessId, status: "open", limit: 1 });
    if (!res.ok) return { state: "failed", message: res.message };
    const { counts } = res.data;
    const all = Object.values(counts).reduce((n, c) => n + c, 0);
    return { state: "ready", data: { freshCount: counts.open, latest: res.data.rows[0] ?? null, open: all - counts.done } };
}

/**
 * Each client's requests, read once for the ids given. Every id starts as
 * loading and the map fills as the reads land. `retry(id)` reads one client again.
 *
 * A read lands only if it is still the newest one for that client: a new set of
 * ids starts a new generation, and a retry outdates the read before it, so a slow
 * answer never overwrites a newer one.
 */
export function useClientRequests(businessIds: string[]): { by: Map<string, BoardRequests>; retry: (id: string) => void } {
    const [by, setBy] = useState<Map<string, BoardRequests>>(() => new Map());
    const key = businessIds.join(",");
    const generation = useRef(0);
    /** The newest read per client in this generation. */
    const latest = useRef(new Map<string, number>());

    const read = useCallback((id: string, run: number) => {
        const token = (latest.current.get(id) ?? 0) + 1;
        latest.current.set(id, token);
        return requestsOf(id).then((r) => {
            if (generation.current === run && latest.current.get(id) === token) setBy((m) => new Map(m).set(id, r));
        });
    }, []);

    useEffect(() => {
        const gen = generation;
        const run = ++gen.current;
        latest.current = new Map();
        const ids = key ? key.split(",") : [];
        if (!REQUESTS_ON || ids.length === 0) {
            setBy(new Map());
            return;
        }
        setBy(new Map(ids.map((id) => [id, { state: "loading" } as BoardRequests])));
        let at = 0;
        const worker = async () => {
            while (at < ids.length && gen.current === run) await read(ids[at++], run);
        };
        void Promise.all(Array.from({ length: Math.min(PARALLEL, ids.length) }, worker));
        return () => {
            // Unmounting or a new set of ids outdates every read still in flight.
            if (gen.current === run) gen.current++;
        };
    }, [key, read]);

    const retry = useCallback(
        (id: string) => {
            if (!key.split(",").includes(id)) return;
            setBy((m) => new Map(m).set(id, { state: "loading" }));
            void read(id, generation.current);
        },
        [key, read],
    );

    return { by, retry };
}
